import { useEffect, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import { Banner, ConfirmationModal } from "@knowtex-ai/components";
import { Home, Notes, Settings, HelpCircle, Book, LogOut } from "iconoir-react";
import styles from "./Sidebar.module.css";
import KnowtexLogo from "./KnowtexLogo";
import sendServerLogging from "../utils/sendServerLogging";
import userJSON from "../utils/userJSON";
import { useUserHelpGuides } from "../hooks/useUserHelpGuides";
import { useLogout } from "../hooks/useLogout";
import { usePylonSsoUrl } from "../hooks/usePylonSsoUrl";
import {
  closePendingPylonTab,
  getPylonSsoRefreshDelayMs,
  isPylonSsoMintFresh,
  openPendingPylonTab,
  openPylonSsoUrl,
  PYLON_RESOURCES_LAUNCH_PATH,
  toTrustedPylonSsoUrl,
} from "../utils/pylonKnowledgeBase";

const logNavigation = (page: string) => {
  sendServerLogging({
    body: {
      ...userJSON(localStorage.getItem("userName")),
      event: "NAVIGATE_PAGE",
      eventData: { current: page },
    },
  });
};

interface NavItemConfig {
  to: string;
  icon: typeof Home;
  label: string;
  id?: string;
  testId: string;
}

const mainNavItems: NavItemConfig[] = [
  { to: "/dashboard", icon: Home, label: "Dashboard", testId: "dashboard-link" },
  { to: "/notes", icon: Notes, label: "All Notes", testId: "all-notes-link" },
];

const helpGuidesNavItem: NavItemConfig = {
  to: "/help-guides",
  icon: Book,
  label: "User Guides",
  testId: "help-guides-link",
};

const settingsNavItem: NavItemConfig = {
  to: "/settings",
  icon: Settings,
  label: "Settings",
  id: "navigate_settings",
  testId: "settings-link",
};

interface SidebarProps {
  /** Clears auth state on logout. */
  setAuth: (isAuthenticated: boolean) => void;
}

/**
 * Always-visible left navigation. Expanded at the `desktop:` breakpoint and
 * above; below it — where the pages used to show a hamburger instead of the
 * nav — it collapses to an icon rail. There is no off-canvas drawer.
 */
export default function Sidebar({ setAuth }: SidebarProps): ReactElement {
  const navigate = useNavigate();
  const logout = useLogout(setAuth);
  const { hasUserHelpGuidesAccess } = useUserHelpGuides();
  const [isRail, setIsRail] = useState(false);
  const [tooltip, setTooltip] = useState<{ label: string; top: number } | null>(null);
  const [resourcesWarningOpen, setResourcesWarningOpen] = useState(false);
  const [prefetchedSso, setPrefetchedSso] = useState<{ url: string; mintedAtMs: number } | null>(
    null
  );
  const { mutateAsync: mintPylonSsoUrl, reset: resetPylonSsoUrl } = usePylonSsoUrl();

  // Labels are only hidden — and so only need a tooltip — in rail mode.
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1279px)");
    const updateIsRail = () => setIsRail(mediaQuery.matches);

    updateIsRail();
    mediaQuery.addEventListener("change", updateIsRail);
    return () => mediaQuery.removeEventListener("change", updateIsRail);
  }, []);

  useEffect(() => {
    if (!isRail) setTooltip(null);
  }, [isRail]);

  const showTooltip = (label: string) => (event: { currentTarget: HTMLElement }) => {
    if (!isRail) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ label, top: rect.top + rect.height / 2 });
  };

  const hideTooltip = () => setTooltip(null);

  // Every item shares these, so hover and keyboard focus behave alike.
  const tooltipHandlers = (label: string) => ({
    onMouseEnter: showTooltip(label),
    onMouseLeave: hideTooltip,
    onFocus: showTooltip(label),
    onBlur: hideTooltip,
  });

  const renderNavItem = ({ to, icon: Icon, label, id, testId }: NavItemConfig) => (
    <NavLink
      key={to}
      to={to}
      id={id}
      data-testid={testId}
      onClick={() => {
        logNavigation(label);
      }}
      className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
      {...tooltipHandlers(label)}
    >
      <span className={styles.navItemIcon}>
        <Icon width={26} height={26} aria-hidden="true" />
      </span>
      <span className={styles.navItemText}>{label}</span>
    </NavLink>
  );

  const goHome = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    logNavigation("Home");
    navigate("/");
  };

  const openResourcesWarning = () => {
    hideTooltip();
    setResourcesWarningOpen(true);
  };

  useEffect(() => {
    if (!resourcesWarningOpen) {
      setPrefetchedSso(null);
      resetPylonSsoUrl();
      return undefined;
    }

    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const mint = () => {
      void mintPylonSsoUrl()
        .then(({ url }) => {
          const trustedUrl = toTrustedPylonSsoUrl(url);
          if (cancelled || !trustedUrl) {
            return;
          }

          const mintedAtMs = Date.now();
          setPrefetchedSso({ url: trustedUrl, mintedAtMs });
          refreshTimer = setTimeout(mint, getPylonSsoRefreshDelayMs(mintedAtMs));
        })
        .catch(() => {
          if (!cancelled) {
            setPrefetchedSso(null);
          }
        });
    };

    mint();

    return () => {
      cancelled = true;
      if (refreshTimer !== undefined) {
        clearTimeout(refreshTimer);
      }
    };
  }, [mintPylonSsoUrl, resetPylonSsoUrl, resourcesWarningOpen]);

  const openResourcesFromModal = (currentUrl: string | null) => {
    logNavigation("Help Center");
    setResourcesWarningOpen(false);

    if (currentUrl && openPylonSsoUrl(currentUrl)) {
      return;
    }

    const pendingTab = openPendingPylonTab();
    void mintPylonSsoUrl()
      .then(({ url }) => {
        const trustedUrl = toTrustedPylonSsoUrl(url);
        if (trustedUrl && openPylonSsoUrl(trustedUrl, pendingTab)) {
          return;
        }

        closePendingPylonTab(pendingTab);
        navigate(PYLON_RESOURCES_LAUNCH_PATH);
      })
      .catch(() => {
        closePendingPylonTab(pendingTab);
        navigate(PYLON_RESOURCES_LAUNCH_PATH);
      });
  };

  const continueToResources = () => {
    const currentUrl =
      prefetchedSso && isPylonSsoMintFresh(prefetchedSso.mintedAtMs) ? prefetchedSso.url : null;
    openResourcesFromModal(currentUrl);
  };

  return (
    <div className={styles.sidebarWrapper}>
      <aside className={styles.sidebar} id="primary-navigation" aria-label="Primary navigation">
        <a
          href="/"
          className={styles.logo}
          onClick={goHome}
          data-testid="knowtex-logo-link"
          aria-label="Knowtex home"
        >
          <span aria-hidden="true">
            <KnowtexLogo wordmarkClassName={styles.logoWordmark} />
          </span>
        </a>

        <nav className={styles.navItems}>
          {mainNavItems.map(renderNavItem)}
          {hasUserHelpGuidesAccess ? renderNavItem(helpGuidesNavItem) : null}
          {renderNavItem(settingsNavItem)}
        </nav>

        <div className={styles.sidebarBottom}>
          <button
            type="button"
            className={styles.navItem}
            onClick={openResourcesWarning}
            data-testid="help-center"
            {...tooltipHandlers("Resources")}
          >
            <span className={styles.navItemIcon}>
              <HelpCircle width={26} height={26} aria-hidden="true" />
            </span>
            <span className={styles.navItemText}>Resources</span>
          </button>
          <button
            type="button"
            className={styles.navItem}
            onClick={() => {
              logout();
            }}
            data-testid="logout-button"
            {...tooltipHandlers("Log out")}
          >
            <span className={styles.navItemIcon}>
              <LogOut width={26} height={26} aria-hidden="true" />
            </span>
            <span className={styles.navItemText}>Log out</span>
          </button>
        </div>
      </aside>

      <ConfirmationModal
        open={resourcesWarningOpen}
        title="Continue to Knowtex Resources?"
        continueText="Continue"
        cancelText="Stay on this page"
        continueVariant="primary-alt"
        onContinue={continueToResources}
        onCancel={() => setResourcesWarningOpen(false)}
      >
        <Banner variant="warning" heading="Resources opens in a new tab" showIcon>
          Continuing will open the Knowtex Knowledge Base in a new tab. Your current Knowtex page
          stays available.
        </Banner>
        {prefetchedSso ? (
          <a
            href={prefetchedSso.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sidebar-resources-open-link"
            className="mt-3 inline-flex text-accent1 underline"
            onClick={(event) => {
              if (isPylonSsoMintFresh(prefetchedSso.mintedAtMs)) {
                setResourcesWarningOpen(false);
                logNavigation("Help Center");
                return;
              }

              event.preventDefault();
              openResourcesFromModal(null);
            }}
          >
            Open Knowtex Resources
          </a>
        ) : null}
      </ConfirmationModal>

      {isRail && tooltip
        ? createPortal(
            <div className={styles.railTooltip} style={{ top: tooltip.top }} aria-hidden="true">
              {tooltip.label}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export { Sidebar };
